import { ArrowRight, ChevronRight } from "lucide-react";

import { AGENT_PROVIDERS } from "@/components/agent-providers/agent-providers";
import { AgentProviderIcon } from "@/components/agent-providers/AgentProviderIcon";
import { Button } from "@/components/ui/Button";
import type { ClientFamily } from "@gram/client/models/components/recordinstallintentrequestbody.js";
import { GramIcon } from "@/components/gram-logo/variants/icon";
import { ModeSwitchStarfield } from "@/components/mode-switch-starfield";
import { PlatformMCPOnboardingContent } from "./PlatformMCP";
import { SourceSurface } from "@gram/client/models/components/startonboardingrequestbody.js";
import { useOrgRoutes } from "@/routes";
import { useSearchParams } from "react-router";
import { useState } from "react";

// The agents the Platform MCP walkthrough supports, with the same brand marks
// and copy the setup wizard uses, and a catch-all last. The catch-all is
// appended here rather than added to the "plugins" surface because that surface
// also drives the marketplace install dialog, which has per-agent plugin
// instructions an uncertified agent has no answer for.
const AGENTS = [
  { id: "claude" as const, ...AGENT_PROVIDERS.claude },
  { id: "claude-cowork" as const, ...AGENT_PROVIDERS["claude-cowork"] },
  { id: "codex" as const, ...AGENT_PROVIDERS.codex },
  { id: "cursor" as const, ...AGENT_PROVIDERS.cursor },
  { id: "opencode" as const, ...AGENT_PROVIDERS.opencode },
  { id: "other" as const, ...AGENT_PROVIDERS.other },
];

const STEPS = [
  { index: "01", label: "Pick your agent" },
  { index: "02", label: "Run one command" },
  { index: "03", label: "Approve access" },
];

function entrySourceFromQuery(value: string | null) {
  return Object.values(SourceSurface).find((surface) => surface === value);
}

function clientFamilyForAgent(agentID: string): ClientFamily {
  switch (agentID) {
    case "claude":
      return "claude_code";
    case "claude-cowork":
      return "claude_cowork";
    case "codex":
    case "cursor":
    case "opencode":
      return agentID;
    default:
      return "other";
  }
}

/**
 * What headless mode shows below the strip: no page chrome, no breadcrumb bar,
 * no onboarding banner — one hero whose whole job is getting an agent
 * connected. The mesh behind the app chrome shows through, so this surface
 * paints no background of its own.
 */
export function HeadlessContent(): JSX.Element {
  return <HeadlessHero />;
}

function HeadlessHero(): JSX.Element {
  const orgRoutes = useOrgRoutes();
  const [searchParams] = useSearchParams();
  // Where the user came from, when they arrived through a dashboard CTA rather
  // than the mode strip. Falls back to the settings surface, which is what
  // landing here directly means.
  const entrySource =
    entrySourceFromQuery(searchParams.get("entrySource")) ??
    SourceSurface.PlatformMcpSettings;
  const [setupOpen, setSetupOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<ClientFamily>();
  const openSetup = () => {
    setSelectedAgent(undefined);
    setSetupOpen(true);
  };
  const openSetupForAgent = (client: ClientFamily) => {
    setSelectedAgent(client);
    setSetupOpen(true);
  };

  return (
    <div className="bg-surface-tertiary-fixed-dark relative flex min-h-full w-full justify-center px-8 pt-20 pb-16">
      {/* The same screensaver the tab grid runs, held at rest behind the hero. */}
      <ModeSwitchStarfield direction={1} className="absolute" />
      {/* One centered column: the hero and the agent list read as a single
          block on the mesh rather than two things pushed to opposite edges. */}
      {/* z-10: the starfield canvas is positioned, so unpositioned content
          would paint beneath it and get crossed by the streaks. */}
      <div className="relative z-10 flex w-full max-w-xl flex-col items-center gap-8 text-center">
        {/* The eyebrow labels the headline, so it sits tight against it
            rather than on the column's own rhythm. */}
        <div className="flex flex-col items-center gap-3">
          {/* The mark, not the wordmark. Its paths ship a fixed ink fill, so
              they are re-pointed at currentColor to sit on the dark surface. */}
          <div
            className="mb-2 [&>svg]:h-9 [&>svg]:w-auto [&_path]:fill-current"
            style={{ color: "var(--text-default-fixed-light)" }}
          >
            <GramIcon />
          </div>
          <span
            className="text-eyebrow"
            style={{ color: "var(--text-muted-fixed-light)" }}
          >
            Platform MCP
          </span>
          <h1 className="text-default-fixed-light font-display text-[48px] leading-[0.9] font-thin tracking-[-0.045em] lg:text-[68px]">
            Connect your agent
          </h1>
        </div>
        <p
          className="max-w-md text-base leading-relaxed"
          style={{ color: "var(--text-muted-fixed-light)" }}
        >
          Use the control plane from the agent you already work in. Your role
          decides which projects, MCP servers, plugins, skills and operational
          data you can access.
        </p>

        <div className="flex flex-col items-center gap-3">
          <Button
            size="lg"
            onClick={openSetup}
            // Inverted for the ink hero: the default primary is ink on ink, so
            // it reads as invisible until hover.
            className="bg-surface-primary-fixed-light hover:bg-surface-tertiary-fixed-light [&_*]:text-default-fixed-dark"
          >
            <Button.Text>Connect your agent</Button.Text>
            <Button.RightIcon>
              <ArrowRight className="h-4 w-4" />
            </Button.RightIcon>
          </Button>
          <span
            className="text-eyebrow"
            style={{ color: "var(--text-muted-fixed-light)" }}
          >
            ~2 minutes
          </span>
        </div>

        {/* Step one, made real: the same agent list the setup sheet opens on,
            so the page itself is the first move rather than a picture of it. */}
        <div className="border-neutral-softest w-full border bg-white/5 text-left backdrop-blur-sm">
          <div className="border-neutral-softest border-b px-5 py-3">
            <p className="text-eyebrow text-muted-fixed-light">
              Choose your agent
            </p>
          </div>
          <ul>
            {AGENTS.map((agent) => (
              <li key={agent.id}>
                <button
                  type="button"
                  onClick={() =>
                    openSetupForAgent(clientFamilyForAgent(agent.id))
                  }
                  className="border-neutral-softest group flex w-full items-center gap-4 border-b px-5 py-3 text-left transition-colors last:border-b-0 hover:bg-white/10"
                >
                  {/* The vendor marks clash out of the box — an orange
                      squircle next to a flat black cube next to a line glyph.
                      Desaturated they read as one set; the row's own hover
                      brings the brand color back.

                      The color is set here because the monochrome marks
                      (Cursor, Codex, opencode, the catch-all globe) are drawn
                      in currentColor: without it they inherit the page's ink
                      foreground and disappear into this dark panel. */}
                  <span className="text-default-fixed-light border-neutral-softest flex h-8 w-8 shrink-0 items-center justify-center border bg-white/5">
                    <AgentProviderIcon
                      source={agent.iconSource}
                      className="h-4 w-4 opacity-80 grayscale transition group-hover:opacity-100 group-hover:grayscale-0"
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="text-default-fixed-light block text-sm font-medium">
                      {agent.name}
                    </span>
                    <span className="text-muted-fixed-light block truncate text-xs">
                      {agent.description}
                    </span>
                  </span>
                  <ChevronRight className="text-muted-fixed-light group-hover:text-default-fixed-light h-4 w-4 shrink-0 transition-colors" />
                </button>
              </li>
            ))}
          </ul>
        </div>

        <ol className="flex flex-wrap justify-center gap-x-8 gap-y-2">
          {STEPS.map((step) => (
            <li key={step.index} className="flex items-baseline gap-2">
              <span
                className="text-eyebrow"
                style={{ color: "var(--text-default-fixed-light)" }}
              >
                {step.index}
              </span>
              <span
                className="text-eyebrow"
                style={{ color: "var(--text-muted-fixed-light)" }}
              >
                {step.label}
              </span>
            </li>
          ))}
        </ol>
      </div>

      {/* The existing walkthrough, driven entirely as sheets: agent picker →
          install method → step-by-step setup. */}
      <PlatformMCPOnboardingContent
        sheetOnly
        setupOpen={setupOpen}
        onSetupOpenChange={setSetupOpen}
        initialSourceSurface={entrySource}
        initialClient={selectedAgent}
        onSetupComplete={() => orgRoutes.home.goTo()}
      />
    </div>
  );
}
